import type {
  Asset,
  AssetType,
  ImageCategory,
  ProjectAssetUsage,
  UpdateAssetRequest,
} from '@gc/shared';
import { ApiError, apiRequest, getCsrfToken } from './client.js';
import type { Pagina } from './projects.js';

export type FiltrosBiblioteca = {
  readonly projectId?: string;
  readonly type?: AssetType;
  readonly category?: ImageCategory;
  readonly q?: string;
  readonly tag?: string;
  readonly status?: 'AVAILABLE' | 'ARCHIVED';
  readonly limit?: number;
  readonly cursor?: string;
};

export async function listarAssets(filtros: FiltrosBiblioteca = {}): Promise<Pagina<Asset>> {
  const params = new URLSearchParams();
  for (const [clave, valor] of Object.entries(filtros)) {
    if (valor !== undefined && valor !== '') params.set(clave, String(valor));
  }
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const respuesta = await apiRequest<{ data: Asset[]; meta: { nextCursor: string | null } }>(
    `/assets${query}`,
    { raw: true },
  );
  return { data: respuesta.data, nextCursor: respuesta.meta.nextCursor };
}

export function actualizarAsset(id: string, input: UpdateAssetRequest): Promise<Asset> {
  return apiRequest<Asset>(`/assets/${id}`, { method: 'PATCH', body: input });
}

export function archivarAsset(id: string): Promise<Asset> {
  return apiRequest<Asset>(`/assets/${id}/archive`, { method: 'POST' });
}

export function restaurarAsset(id: string): Promise<Asset> {
  return apiRequest<Asset>(`/assets/${id}/restore`, { method: 'POST' });
}

export function asociarAsset(
  projectId: string,
  assetId: string,
  usage: ProjectAssetUsage = 'LIBRARY',
): Promise<Asset> {
  return apiRequest<Asset>(`/projects/${projectId}/assets`, {
    method: 'POST',
    body: { assetId, usage },
  });
}

export type SubidaProgreso = { readonly enviados: number; readonly total: number };

/**
 * La subida usa XMLHttpRequest y no `fetch` porque necesita progreso: `fetch`
 * no expone el avance del cuerpo que se envía.
 */
export function subirAsset(
  projectId: string,
  archivo: File,
  opciones: {
    readonly category?: ImageCategory;
    readonly tags?: readonly string[];
    onProgreso?(progreso: SubidaProgreso): void;
  },
): Promise<Asset> {
  return new Promise((resolver, rechazar) => {
    const csrfToken = getCsrfToken();
    if (csrfToken === undefined) {
      rechazar(new ApiError('AUTH_ERROR', 'Sesión no iniciada.', 401));
      return;
    }

    const cuerpo = new FormData();
    if (opciones.category !== undefined) cuerpo.append('category', opciones.category);
    if (opciones.tags !== undefined && opciones.tags.length > 0) {
      cuerpo.append('tags', opciones.tags.join(','));
    }
    cuerpo.append('file', archivo, archivo.name);

    const peticion = new XMLHttpRequest();
    peticion.open('POST', `/api/v1/projects/${projectId}/assets/upload`);
    peticion.withCredentials = true;
    peticion.setRequestHeader('x-csrf-token', csrfToken);

    peticion.upload.addEventListener('progress', (evento) => {
      if (evento.lengthComputable) {
        opciones.onProgreso?.({ enviados: evento.loaded, total: evento.total });
      }
    });

    peticion.addEventListener('load', () => {
      let cuerpoRespuesta: unknown;
      try {
        cuerpoRespuesta = JSON.parse(peticion.responseText);
      } catch {
        rechazar(new ApiError('INTERNAL_ERROR', 'Respuesta ilegible del servidor.', peticion.status));
        return;
      }
      if (peticion.status >= 200 && peticion.status < 300) {
        resolver((cuerpoRespuesta as { data: Asset }).data);
        return;
      }
      const error = (cuerpoRespuesta as { error?: { code?: string; message?: string } }).error;
      rechazar(
        new ApiError(
          (error?.code as never) ?? 'INTERNAL_ERROR',
          error?.message ?? 'No se pudo subir el archivo.',
          peticion.status,
        ),
      );
    });

    peticion.addEventListener('error', () => {
      rechazar(new ApiError('INTERNAL_ERROR', 'Error de red al subir el archivo.', 0));
    });

    peticion.send(cuerpo);
  });
}
